import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import './../../components/CustomComponents/Switchery/ToggleSwitch.css';
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageRole = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const isChildBranch = useIsChildBranch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [originalRoleCount, setOriginalRoleCount] = useState(0);

  const pageKey = "roles";
  const defaultVisibleColumns = {
    name: true,
    backOffice: true,
    allowRefunds: true,
    allowBasketDiscounts: true,
    allowPettyCash: true,
    actions: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Role_Orderby", label: "Order By" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const sortByOptions = [
    { value: "R_Title", label: "Name" },
  ];

  useEffect(() => {
    if (!CompID || CompID <= 0) {
      showErrorToast("Invalid Company ID. Please log in again.");
      navigate("/login");
    }
  }, [CompID, navigate]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const clearFilters = () => {
    setSearch("");
  };

  const fetchRoleData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });
      console.log("Roles Response:", response);

      const roleData = response.data.data || response.data;
      if (Array.isArray(roleData)) {
        setRoles(roleData);
        if (!search.trim()) {
          setOriginalRoleCount(roleData.length);
        }
      } else {

        setRoles([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);

      showErrorToast("Failed to fetch roles.");
    }
  }, [search, sortBy, sortOrder, CompID]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRoleData();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchRoleData]);

  const handleAddRole = () => navigate("/role/addStaffRole");

  const handleDeleteRole = async (roleItem) => {
    try {


      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete role ${roleItem.r_Title}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) {
        setLoading(false);
        return;
      }

      if (!roleItem.comRolePermission_ID || !CompID || CompID <= 0) {
        showErrorToast("Invalid role or company ID.");
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ROLE, {
        params: {
          CompID,
          id: roleItem.comRolePermission_ID,
        },
      });

      if (response.data.status) {
        setRoles((prevRoles) =>
          prevRoles.filter((item) => item.comRolePermission_ID !== roleItem.comRolePermission_ID)
        );
        showSuccessToast("Role deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {

      showErrorToast("Something went wrong while deleting the role.");
      setLoading(false);
    }
  };

  const renderNoDataMessage = () => {
    if (search.trim() && roles.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No roles found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  const isPermanentRole = (role) => role.isPermanentRole === true || role.isPermanentRole === 1;

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Staff Roles</li>
          </ol>
          <h1 className="page-header mb-0">Manage Staff Roles</h1>
        </div>
        {!isChildBranch && (
          <div className="ms-auto">
            <button
              className="btn px-4"
              onClick={handleAddRole}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Staff Role
            </button>
          </div>
        )}
      </div>

      {!isChildBranch && !loading && originalRoleCount === 0 && (
        <AddButton onClick={handleAddRole} title="Add Role" />
      )}

      {(originalRoleCount > 0 || search.trim()) && (
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by staff role ..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {renderNoDataMessage()}

              {roles.length > 0 && (
                <div className="table-responsive mt-3" style={{ minHeight: "200px" }}>
                  <table className="table table-hover">
                    <thead
                      className="bg-light"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2"></th>
                        {visibleColumns.name && (
                          <th className="pt-2 pb-2">Role Name</th>
                        )}
                        {visibleColumns.backOffice && (
                          <th className="pt-2 pb-2 text-center">Back Office</th>
                        )}
                        {visibleColumns.allowRefunds && (
                          <th className="pt-2 pb-2 text-center">Allow Refunds</th>
                        )}
                        {visibleColumns.allowBasketDiscounts && (
                          <th className="pt-2 pb-2 text-center">Allow Basket Discounts</th>
                        )}
                        {visibleColumns.allowPettyCash && (
                          <th className="pt-2 pb-2 text-center">Allow Petty Cashes</th>
                        )}
                        {visibleColumns.actions && (
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role, index) => (
                        <tr key={role.comRolePermission_ID}>
                          <td className="align-middle"></td>
                          {visibleColumns.name && (
                            <td
                              className="align-middle"
                              style={{
                                cursor: !isChildBranch ? "pointer" : "default"
                              }}
                              onClick={() => {
                                if (!isChildBranch) {
                                  navigate(`/role/updateStaffRole/${role.comRolePermission_ID}`);
                                }
                              }}
                            >
                              {role.r_Title}
                            </td>
                          )}
                          {visibleColumns.backOffice && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${role.isBackOffice ? "active" : ""}`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={role.isBackOffice}
                                    readOnly
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: role.isBackOffice ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          {visibleColumns.allowRefunds && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${role.allowRefunds ? "active" : ""}`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={role.allowRefunds}
                                    readOnly
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: role.allowRefunds ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          {visibleColumns.allowBasketDiscounts && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${role.allowBasketDiscounts ? "active" : ""}`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={role.allowBasketDiscounts}
                                    readOnly
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: role.allowBasketDiscounts ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          {visibleColumns.allowPettyCash && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${role.allowPettyCashes ? "active" : ""}`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={role.allowPettyCashes}
                                    readOnly
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: role.allowPettyCashes ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          {visibleColumns.actions && (
                            <td className="align-middle text-center">
                              {!isPermanentRole(role) ? (
                                <div className="d-flex justify-content-center gap-2">
                                  {/* Edit Icon - hidden for branch */}
                                  {!isChildBranch && (
                                    <Link
                                      to={`/role/updateStaffRole/${role.comRolePermission_ID}`}
                                      className="btn btn-sm btn-outline-primary"
                                      title="Edit Role"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        border: `1px solid ${themeColor}`,
                                        color: themeColor,
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = themeColor;
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = themeColor;
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }}
                                    >
                                      <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                    </Link>
                                  )}

                                  {/* Delete Icon */}
                                  {!isChildBranch && (
                                    <button
                                      onClick={() => handleDeleteRole(role)}
                                      className="btn btn-sm btn-outline-danger"
                                      title="Delete Role"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        border: '1px solid #dc3545',
                                        color: '#dc3545',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dc3545';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#dc3545';
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }}
                                    >
                                      <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex justify-content-center gap-2">
                                  {!isChildBranch && (
                                    <Link
                                      to={`/role/updateStaffRole/${role.comRolePermission_ID}`}
                                      className="btn btn-sm btn-outline-secondary"
                                      title="View Permanent Role"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      <i className="fa fa-eye" style={{ fontSize: '12px' }}></i>
                                    </Link>
                                  )}
                                  <div className="text-muted d-flex align-items-center" style={{ fontSize: '16px' }} title="Permanent Role">
                                    <i className="fa fa-lock"></i>
                                  </div>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageRole;